import { useEffect, useState } from 'react'
import { getBudgets, saveBudgets, createHousehold, joinHousehold, signOut,
         getCustomCategories, addCustomCategory, deleteCustomCategory,
         getCustomModes, addCustomMode, deleteCustomMode } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { DEFAULT_CATEGORIES, DEFAULT_MODES, CATEGORY_EMOJIS, CATEGORY_COLORS, MODE_EMOJIS } from '../lib/constants'

export default function SettingsPage({ onCatsChanged }) {
  const { user, household, refreshHousehold } = useAuth()
  const [budgets, setBudgets]     = useState({})
  const [saving,  setSaving]      = useState(false)
  const [saved,   setSaved]       = useState(false)
  const [hhName,  setHhName]      = useState('')
  const [code,    setCode]        = useState('')
  const [hhMsg,   setHhMsg]       = useState('')
  const [tab,     setTab]         = useState('budgets')

  // custom categories
  const [customCats, setCustomCats] = useState([])
  const [newLabel,   setNewLabel]   = useState('')
  const [newIcon,    setNewIcon]    = useState('📦')
  const [newColor,   setNewColor]   = useState(CATEGORY_COLORS[0])
  const [catMsg,     setCatMsg]     = useState('')

  // custom modes
  const [customModes, setCustomModes] = useState([])
  const [newModeLabel, setNewModeLabel] = useState('')
  const [newModeIcon,  setNewModeIcon]  = useState('💸')
  const [modeMsg,      setModeMsg]      = useState('')

  const allCats = [...DEFAULT_CATEGORIES, ...customCats]
  const allModes = [...DEFAULT_MODES, ...customModes]

  useEffect(() => {
    getBudgets(user.id, household?.id).then(setBudgets)
    getCustomCategories(user.id).then(setCustomCats)
    getCustomModes(user.id).then(setCustomModes)
  }, [household])

  const set = cat => e => {
    const val = e.target.value
    setBudgets(b => ({ ...b, [cat]: val === '' ? undefined : +val }))
  }

  const saveLimits = async () => {
    setSaving(true)
    const clean = Object.fromEntries(Object.entries(budgets).filter(([, v]) => v > 0))
    await saveBudgets(clean, user.id, household?.id)
    setSaving(false); setSaved(true)
    setTimeout(() => setSaved(false), 2000)
  }

  const create = async () => {
    if (!hhName.trim()) return
    try {
      const hh = await createHousehold(hhName, user.id)
      setHhMsg(`Household created! Invite code: ${hh.invite_code}`)
      refreshHousehold()
    } catch (e) { setHhMsg('Error: ' + e.message) }
  }

  const join = async () => {
    if (!code.trim()) return
    try {
      await joinHousehold(code, user.id)
      setHhMsg('Joined successfully!')
      refreshHousehold()
    } catch (e) { setHhMsg('Error: ' + e.message) }
  }

  const addCat = async () => {
    if (!newLabel.trim()) { setCatMsg('Please enter a category name'); return }
    if (allCats.find(c => c.label.toLowerCase() === newLabel.trim().toLowerCase())) {
      setCatMsg('Category already exists'); return
    }
    try {
      const cat = await addCustomCategory(user.id, newLabel.trim(), newIcon, newColor)
      setCustomCats(prev => [...prev, cat])
      setNewLabel(''); setCatMsg('Category added!')
      setTimeout(() => setCatMsg(''), 2000)
      if (onCatsChanged) onCatsChanged()
    } catch (e) { setCatMsg('Error: ' + e.message) }
  }

  const addMode = async () => {
    if (!newModeLabel.trim()) { setModeMsg('Please enter a mode name'); return }
    if (allModes.find(m => m.label.toLowerCase() === newModeLabel.trim().toLowerCase())) {
      setModeMsg('Mode already exists'); return
    }
    try {
      const m = await addCustomMode(user.id, newModeLabel.trim(), newModeIcon)
      setCustomModes(prev => [...prev, m])
      setNewModeLabel(''); setModeMsg('Mode added!')
      setTimeout(() => setModeMsg(''), 2000)
    } catch (e) { setModeMsg('Error: ' + e.message) }
  }

  const delMode = async (id) => {
    if (!window.confirm('Delete this payment mode?')) return
    await deleteCustomMode(id)
    setCustomModes(prev => prev.filter(m => m.id !== id))
  }

  const delCat = async (id) => {
    if (!window.confirm('Delete this category?')) return
    await deleteCustomCategory(id)
    setCustomCats(prev => prev.filter(c => c.id !== id))
    if (onCatsChanged) onCatsChanged()
  }

  return (
    <div style={{ padding: '1rem' }}>
      <div style={s.tabs}>
        {['budgets', 'categories', 'modes', 'family', 'account'].map(t => (
          <button key={t} style={{ ...s.tab, ...(tab===t ? s.tabActive : {}) }} onClick={() => setTab(t)}>
            {t.charAt(0).toUpperCase()+t.slice(1)}
          </button>
        ))}
      </div>

      {/* BUDGETS */}
      {tab === 'budgets' && (
        <div style={s.card}>
          <div style={s.cardTitle}>Monthly budget limits</div>
          <p style={s.hint}>Set limits per category. You'll get a nudge at 80% and when you cross 100%.</p>
          {allCats.map(c => (
            <div key={c.label} style={s.budRow}>
              <span style={{ fontSize: 16 }}>{c.icon}</span>
              <span style={{ flex: 1, fontSize: 14 }}>{c.label}</span>
              <div style={s.inputWrap}>
                <span style={s.rupeeSmall}>₹</span>
                <input style={s.budInput} type="number" placeholder="No limit"
                  value={budgets[c.label] || ''} onChange={set(c.label)} />
              </div>
            </div>
          ))}
          {saved && <div style={s.success}>✓ Saved!</div>}
          <button style={{ ...s.btn, opacity: saving ? 0.6 : 1 }} onClick={saveLimits} disabled={saving}>
            {saving ? 'Saving…' : 'Save limits'}
          </button>
        </div>
      )}

      {/* CUSTOM CATEGORIES */}
      {tab === 'categories' && (
        <div>
          <div style={s.card}>
            <div style={s.cardTitle}>Add custom category</div>
            <p style={s.hint}>Add categories that suit your lifestyle — Petrol, Kids, Travel, Gym, etc.</p>

            <div style={s.label}>Category name</div>
            <input style={s.input} placeholder="e.g. Petrol, Kids, Travel…"
              value={newLabel} onChange={e => setNewLabel(e.target.value)} />

            <div style={s.label}>Pick an icon</div>
            <div style={s.emojiGrid}>
              {CATEGORY_EMOJIS.map(em => (
                <button key={em} style={{ ...s.emojiBtn, ...(newIcon === em ? s.emojiActive : {}) }}
                  onClick={() => setNewIcon(em)}>{em}</button>
              ))}
            </div>

            <div style={s.label}>Pick a colour</div>
            <div style={s.colorGrid}>
              {CATEGORY_COLORS.map(col => (
                <button key={col} style={{ ...s.colorBtn, background: col, ...(newColor === col ? s.colorActive : {}) }}
                  onClick={() => setNewColor(col)} />
              ))}
            </div>

            <div style={s.preview}>
              Preview: <span style={{ ...s.previewChip, background: newColor }}>{newIcon} {newLabel || 'My category'}</span>
            </div>

            {catMsg && <div style={catMsg.includes('Error') ? s.error : s.success}>{catMsg}</div>}
            <button style={s.btn} onClick={addCat}>Add category</button>
          </div>

          {customCats.length > 0 && (
            <div style={s.card}>
              <div style={s.cardTitle}>Your custom categories</div>
              {customCats.map(c => (
                <div key={c.id} style={s.catRow}>
                  <span style={{ ...s.catDot, background: c.color }}>{c.icon}</span>
                  <span style={{ flex: 1, fontSize: 14 }}>{c.label}</span>
                  <button onClick={() => delCat(c.id)} style={s.delBtn}>Delete</button>
                </div>
              ))}
            </div>
          )}

          <div style={s.card}>
            <div style={s.cardTitle}>Default categories</div>
            {DEFAULT_CATEGORIES.map(c => (
              <div key={c.label} style={s.catRow}>
                <span style={{ ...s.catDot, background: c.color }}>{c.icon}</span>
                <span style={{ flex: 1, fontSize: 14 }}>{c.label}</span>
                <span style={{ fontSize: 11, color: '#aaa' }}>Default</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* MODES */}
      {tab === 'modes' && (
        <div>
          <div style={s.card}>
            <div style={s.cardTitle}>Add custom payment mode</div>
            <p style={s.hint}>Add modes like Cheque, NEFT, Amazon Pay, Bajaj EMI, etc.</p>
            <div style={s.label}>Mode name</div>
            <input style={s.input} placeholder="e.g. Cheque, NEFT, Amazon Pay…"
              value={newModeLabel} onChange={e => setNewModeLabel(e.target.value)} />
            <div style={s.label}>Pick an icon</div>
            <div style={s.emojiGrid}>
              {MODE_EMOJIS.map(em => (
                <button key={em} style={{ ...s.emojiBtn, ...(newModeIcon === em ? s.emojiActive : {}) }}
                  onClick={() => setNewModeIcon(em)}>{em}</button>
              ))}
            </div>
            <div style={s.preview}>Preview: <span style={{ fontWeight: 600 }}>{newModeIcon} {newModeLabel || 'My mode'}</span></div>
            {modeMsg && <div style={modeMsg.includes('Error') ? s.error : s.success}>{modeMsg}</div>}
            <button style={s.btn} onClick={addMode}>Add mode</button>
          </div>

          {customModes.length > 0 && (
            <div style={s.card}>
              <div style={s.cardTitle}>Your custom modes</div>
              {customModes.map(m => (
                <div key={m.id} style={s.catRow}>
                  <span style={{ fontSize: 20, marginRight: 10 }}>{m.icon}</span>
                  <span style={{ flex: 1, fontSize: 14 }}>{m.label}</span>
                  <button onClick={() => delMode(m.id)} style={s.delBtn}>Delete</button>
                </div>
              ))}
            </div>
          )}

          <div style={s.card}>
            <div style={s.cardTitle}>Default payment modes</div>
            {DEFAULT_MODES.map(m => (
              <div key={m.label} style={s.catRow}>
                <span style={{ fontSize: 20, marginRight: 10 }}>{m.icon}</span>
                <span style={{ flex: 1, fontSize: 14 }}>{m.label}</span>
                <span style={{ fontSize: 11, color: '#aaa' }}>Default</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* FAMILY */}
      {tab === 'family' && (
        <div>
          {household ? (
            <div style={s.card}>
              <div style={s.cardTitle}>Your household</div>
              <div style={s.hhBox}>
                <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>{household.name}</div>
                <div style={{ fontSize: 13, color: '#888', marginBottom: 12 }}>Share this code with your family:</div>
                <div style={s.codeBox}>{household.invite_code}</div>
                <div style={{ fontSize: 12, color: '#aaa', marginTop: 8 }}>Anyone with this code can join and see shared expenses.</div>
              </div>
            </div>
          ) : (
            <div>
              <div style={s.card}>
                <div style={s.cardTitle}>Create a household</div>
                <p style={s.hint}>Create a shared space — your family logs together, sees the same data.</p>
                <input style={s.input} placeholder="Household name (e.g. Sharma Family)" value={hhName} onChange={e => setHhName(e.target.value)} />
                <button style={s.btn} onClick={create}>Create household</button>
              </div>
              <div style={s.card}>
                <div style={s.cardTitle}>Join a household</div>
                <p style={s.hint}>Got an invite code from your partner or family member?</p>
                <input style={s.input} placeholder="Enter invite code" value={code} onChange={e => setCode(e.target.value.toUpperCase())} maxLength={6} />
                <button style={s.btn} onClick={join}>Join</button>
              </div>
            </div>
          )}
          {hhMsg && <div style={s.success}>{hhMsg}</div>}
        </div>
      )}

      {/* ACCOUNT */}
      {tab === 'account' && (
        <div style={s.card}>
          <div style={s.cardTitle}>Account</div>
          <div style={s.acctRow}><span style={s.acctLabel}>Email</span><span style={s.acctVal}>{user.email}</span></div>
          <div style={s.acctRow}><span style={s.acctLabel}>Name</span><span style={s.acctVal}>{user.user_metadata?.name || '—'}</span></div>
          {household && <div style={s.acctRow}><span style={s.acctLabel}>Household</span><span style={s.acctVal}>{household.name}</span></div>}
          <div style={s.acctRow}>
      <span style={s.acctLabel}>Subscription</span>
      <span style={{ ...s.acctVal, color: '#2D9B6F' }}>Free until 8 Jul 2026</span>
    </div>
          <button style={{ ...s.btn, background: '#FDF0EE', color: '#C0392B', marginTop: 24 }} onClick={() => { if(window.confirm('Log out?')) signOut() }}>
            Log out
          </button>
        </div>
      )}
    </div>
  )
}

const s = {
  tabs:        { display: 'flex', gap: 4, background: '#F7F4EF', borderRadius: 10, padding: 4, marginBottom: 16, flexWrap: 'wrap' },
  tab:         { flex: 1, padding: '8px 4px', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 500, background: 'transparent', color: '#888', minWidth: 60 },
  tabActive:   { background: '#fff', color: '#1A1A2E', boxShadow: '0 1px 4px rgba(0,0,0,0.1)' },
  card:        { background: '#fff', border: '1.5px solid #EEE', borderRadius: 16, padding: '1rem 1.25rem', marginBottom: 14 },
  cardTitle:   { fontSize: 13, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 10 },
  hint:        { fontSize: 13, color: '#888', marginBottom: 14, lineHeight: 1.5 },
  label:       { fontSize: 12, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 8, marginTop: 12 },
  budRow:      { display: 'flex', alignItems: 'center', gap: 12, paddingTop: 10, borderTop: '1px solid #F5F5F5' },
  inputWrap:   { display: 'flex', alignItems: 'center', border: '1.5px solid #E8E4DC', borderRadius: 8, overflow: 'hidden', background: '#FAFAF8' },
  rupeeSmall:  { padding: '0 6px 0 10px', color: '#888', fontSize: 14 },
  budInput:    { width: 90, padding: '8px 8px 8px 0', border: 'none', background: 'transparent', fontSize: 14, color: '#1A1A2E', outline: 'none', fontFamily: 'inherit' },
  btn:         { display: 'block', width: '100%', padding: 13, marginTop: 16, background: '#1A1A2E', color: '#E8C547', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: 'pointer' },
  success:     { background: '#EAFAF1', color: '#1E8449', padding: '10px 14px', borderRadius: 10, margin: '12px 0', fontSize: 14, fontWeight: 600 },
  error:       { background: '#FDF0EE', color: '#C0392B', padding: '10px 14px', borderRadius: 10, margin: '12px 0', fontSize: 14 },
  input:       { width: '100%', padding: '11px 14px', border: '1.5px solid #E8E4DC', borderRadius: 10, fontSize: 14, background: '#FAFAF8', color: '#1A1A2E', boxSizing: 'border-box', fontFamily: 'inherit', outline: 'none' },
  emojiGrid:   { display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 },
  emojiBtn:    { fontSize: 20, padding: '6px', border: '1.5px solid #E8E4DC', borderRadius: 8, background: '#FAFAF8', cursor: 'pointer' },
  emojiActive: { border: '2px solid #1A1A2E', background: '#F0F0F0' },
  colorGrid:   { display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 },
  colorBtn:    { width: 28, height: 28, borderRadius: '50%', border: '2px solid transparent', cursor: 'pointer' },
  colorActive: { border: '3px solid #1A1A2E', transform: 'scale(1.2)' },
  preview:     { fontSize: 13, color: '#888', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 },
  previewChip: { color: '#fff', padding: '4px 12px', borderRadius: 20, fontSize: 13, fontWeight: 600 },
  catRow:      { display: 'flex', alignItems: 'center', gap: 10, paddingTop: 10, borderTop: '1px solid #F5F5F5' },
  catDot:      { width: 32, height: 32, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 },
  delBtn:      { background: 'none', border: 'none', color: '#E74C3C', fontSize: 12, cursor: 'pointer', padding: 0 },
  hhBox:       { background: '#F7F4EF', borderRadius: 12, padding: '1rem' },
  codeBox:     { background: '#1A1A2E', color: '#E8C547', fontFamily: 'monospace', fontSize: 28, fontWeight: 700, letterSpacing: 8, padding: '14px 20px', borderRadius: 10, textAlign: 'center' },
  acctRow:     { display: 'flex', justifyContent: 'space-between', paddingTop: 12, borderTop: '1px solid #F5F5F5', fontSize: 14 },
  acctLabel:   { color: '#888' },
  acctVal:     { fontWeight: 600, color: '#1A1A2E' },
}
